import React, { useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Button,
  Text,
  Image,
  IconButton,
  Collapse,
  Divider,
  useBreakpointValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";
import { FiUser } from "react-icons/fi";
import { useUser } from "../../contexts/UserContext";
import { logoutToLanding } from "../../lib/logout";
import { getPlatform } from "../../lib/platformUtils";

// iOS 만 status bar 만큼 nav 상단 패딩 강제 (기존 py={3}=0.75rem 보존). Android/Web 은 이전 동작 유지.
const TOP_INSET_STYLE = getPlatform() === "ios"
  ? { paddingTop: "calc(max(env(safe-area-inset-top), 47px) + 0.75rem)" }
  : undefined;

const VIDEO_COMMERCE_BASE_PATH = "/video-analysis";

const VideoCommerceLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isDesktop = useBreakpointValue({ base: false, md: true });

  const menuItems = useMemo(
    () => [
      { id: "home", label: "홈", path: VIDEO_COMMERCE_BASE_PATH },
      { id: "register", label: "영상 등록", path: `${VIDEO_COMMERCE_BASE_PATH}/register` },
      { id: "results", label: "분석 리포트", path: `${VIDEO_COMMERCE_BASE_PATH}/results` },
    ],
    []
  );

  const isActivePath = (path) => {
    if (path === VIDEO_COMMERCE_BASE_PATH) {
      return location.pathname === VIDEO_COMMERCE_BASE_PATH;
    }
    return location.pathname === path || location.pathname.startsWith(path + "/");
  };

  return (
    <Flex direction="column" minH="100vh" bg="#f7fafc">
      <Box bg="white" borderBottom="1px" borderColor="gray.200" px={4} py={3} position="sticky" top={0} zIndex={1000} style={TOP_INSET_STYLE}>
        <Flex align="center" maxW="1400px" mx="auto" justify="space-between" gap={4}>
          <Flex align="center" gap={6} flex={1}>
            <Flex align="center" gap={2} cursor="pointer" onClick={() => navigate(VIDEO_COMMERCE_BASE_PATH)}>
              <Image src="/laeebly-icon.png" alt="Laeebly" w="8" h="8" borderRadius="lg" objectFit="contain" />
              <Box>
                <Text fontSize="xl" fontWeight="semibold">
                  AI 커머스 최적화 솔루션
                </Text>
                <Text fontSize="xs" color="gray.500">
                Laeebly AI Solution
                </Text>
              </Box>
            </Flex>

            {userInfo?.email && isDesktop && (
              <Flex as="nav" gap={2}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    size="sm"
                    variant={isActivePath(item.path) ? "solid" : "ghost"}
                    colorScheme={isActivePath(item.path) ? "brand" : "gray"}
                    onClick={() => navigate(item.path)}
                  >
                    {item.label}
                  </Button>
                ))}
              </Flex>
            )}
          </Flex>

          <Flex align="center" gap={2}>
            {userInfo?.email && (
              <>
                {isDesktop ? (
                  <Menu>
                    <MenuButton
                      as={Button}
                      size="sm"
                      variant="outline"
                      borderRadius="md"
                      leftIcon={<FiUser />}
                      px={3}
                    >
                      {userInfo.name || userInfo.email}
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={() => navigate("/settings")}>설정</MenuItem>
                      <MenuItem onClick={() => navigate("/home")}>레이블리</MenuItem>
                      <Divider my={1} />
                      <MenuItem onClick={logoutToLanding}>로그아웃</MenuItem>
                    </MenuList>
                  </Menu>
                ) : (
                  <IconButton
                    aria-label="모바일 메뉴"
                    variant="ghost"
                    icon={mobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
                    onClick={() => setMobileMenuOpen((v) => !v)}
                  />
                )}
              </>
            )}
          </Flex>
        </Flex>

        {!isDesktop && userInfo?.email && (
          <Collapse in={mobileMenuOpen} animateOpacity>
            <Box borderTop="1px" borderColor="gray.200" mt={3} pt={3}>
              <Flex direction="column" gap={2}>
                {menuItems.map((item) => (
                  <Button
                    key={item.id}
                    justifyContent="flex-start"
                    variant={isActivePath(item.path) ? "solid" : "ghost"}
                    colorScheme={isActivePath(item.path) ? "brand" : "gray"}
                    onClick={() => {
                      navigate(item.path);
                      setMobileMenuOpen(false);
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
                <Divider />
                <Button justifyContent="flex-start" variant="ghost" onClick={() => {
                  navigate("/settings");
                  setMobileMenuOpen(false);
                }}>
                  설정
                </Button>
                <Button justifyContent="flex-start" variant="ghost" onClick={() => {
                  navigate("/home");
                  setMobileMenuOpen(false);
                }}>
                  레이블리
                </Button>
                <Divider />
                <Button justifyContent="flex-start" variant="ghost" onClick={() => navigate("/edit-profile")}>
                  프로필 수정
                </Button>
                <Button justifyContent="flex-start" colorScheme="red" variant="outline" onClick={logoutToLanding}>
                  로그아웃
                </Button>
              </Flex>
            </Box>
          </Collapse>
        )}
      </Box>

      <Box flex={1} p={{ base: 6, md: 10 }} maxW="1400px" w="100%" mx="auto">
        <Outlet />
      </Box>

      <Box bg="gray.900" color="gray.300" mt="auto">
        <Box maxW="1400px" mx="auto" px={8} py={8}>
          <Flex direction={{ base: "column", md: "row" }} gap={8}>
            <Box flex={1}>
              <Flex align="center" gap={2} mb={4}>
                <Image src="/laeebly-icon.png" alt="Laeebly" w="6" h="6" borderRadius="md" objectFit="contain" />
                <Text>AI 커머스 최적화 솔루션</Text>
              </Flex>
              <Text fontSize="sm" color="gray.400">
                AI 기반 영상 분석 및 상품 인식 솔루션
              </Text>
            </Box>

            <Box flex={1}>
              <Text mb={4}>서비스</Text>
              <Flex direction="column" gap={2} fontSize="sm" color="gray.400">
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/terms-of-service")}>
                  이용약관
                </Button>
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/privacy-policy")}>
                  개인정보처리방침
                </Button>
                <Button variant="link" colorScheme="whiteAlpha" justifyContent="flex-start" onClick={() => navigate("/home")}>
                  레이블리
                </Button>
              </Flex>
            </Box>

            <Box flex={1}>
              <Text mb={4}>문의</Text>
              <Flex direction="column" gap={2} fontSize="sm" color="gray.400">
                <Text>(주) 루나르트 | 대표 권재의</Text>
                <Text>사업자등록번호 652-88-00793</Text>
                <Text>서울특별시 성동구 연무장5가길 25, 8층 804호(성수동2가, 성수역 SKV1 Tower)</Text>
                <Text>contact@laeebly.com</Text>
              </Flex>
            </Box>
          </Flex>

          <Box borderTop="1px" borderColor="gray.800" mt={8} pt={8} textAlign="center" fontSize="sm" color="gray.500">
            © {new Date().getFullYear()} Laeebly Video Commerce. All rights reserved.
          </Box>
        </Box>
      </Box>
    </Flex>
  );
};

export default VideoCommerceLayout;


